<template>
  <v-row v-if="shouldShow">
    <v-col class="pa-2 mb-3" cols="12">
      <div class="demo-countdown-notice">
        <v-icon size="24" icon="$warning" class="mr-2" />
        <span class="body2 align-self-center" v-html="descriptionText"> </span>

        <v-btn
          class="ml-4 demo-countdown-notice__button"
          rounded
          variant="flat"
          size="small"
          color="primary"
          data-testid="request-trial-btn"
          @click="openStartTrialDialog"
        >
          <v-icon size="18" icon="$playXs" color="white" class="mr-2" />
          <span>{{ $t("demoCountdownNotice.requestTrial") }}</span>
        </v-btn>
      </div>
    </v-col>
  </v-row>
</template>
<script lang="ts">
import { computed, defineComponent } from "vue";
import { getTimeUntil } from "@/_helpers/date";
import { isWorkspaceNewAndInDemoMode } from "@/_helpers/subscription";
import { storeToRefs } from "pinia";
import { useDialogsStore } from "@/_store/dialogs.module.ts";
import { ModalWidth } from "@/constants/modals.ts";
import { TutorialAction } from "@/constants/tutorial.ts";
import { useWorkspaceStore } from "@/_store/workspace.module.ts";
import { SubscriptionType, WorkspaceType } from "@/constants/workplaces.ts";
import { useI18n } from "vue-i18n";
import { dialogsConfigConstructor } from "@/_helpers/dialog-helpers.ts";

export default defineComponent({
  setup() {
    const i18n = useI18n();
    const workspaceStore = useWorkspaceStore();
    const { workspace } = storeToRefs(workspaceStore);
    const dialogsStore = useDialogsStore();

    const isNewAndInDemoMode = computed(() => isWorkspaceNewAndInDemoMode());
    const isFrozen = computed(() => workspace.value?.subscriptionType === SubscriptionType.FREEZE);

    const shouldShow = computed(() => {
      const { type, trialAvailable } = workspace.value;
      if (type !== WorkspaceType.REGULAR || !trialAvailable) return false;
      return isNewAndInDemoMode.value || isFrozen.value;
    });

    const descriptionText = computed(() => {
      if (isNewAndInDemoMode.value) {
        return i18n.t("demoCountdownNotice.demoModeFinishes", {
          time: getTimeUntil(workspace.value.demoModeEndDate),
        });
      }
      if (isFrozen.value) {
        return i18n.t("general.workspaceCurrentlyInactive");
      }
      return "";
    });

    const openStartTrialDialog = async () => {
      try {
        await workspaceStore.requestTrialForCurrentWorkspace();
      } catch {
        return;
      }

      dialogsStore.openDialog(
        dialogsConfigConstructor({
          width: ModalWidth.SMALL,
          header: {
            title: i18n.t(`modals.${TutorialAction.REQUEST_WORKSPACE_TRIAL}.title`),
          },
          content: {
            html: i18n.t(`modals.${TutorialAction.REQUEST_WORKSPACE_TRIAL}.description`),
          },
          footer: {
            buttons: [
              {
                title: i18n.t("general.ok"),
                color: "primary",
                spacer: "before",
                callback: dialogsStore.closeDialog,
                cancel: false,
              },
            ],
          },
        })
      );
    };

    return {
      shouldShow,
      descriptionText,
      openStartTrialDialog,
    };
  },
});
</script>

<style lang="scss" scoped>
.demo-countdown-notice {
  display: flex;
  align-items: center;
  padding: 8px 24px;
  background: rgb(var(--v-theme-purple-faint));
  border: 1px solid rgb(var(--v-theme-labels-purple-full));
  border-radius: 8px;

  &__button {
    --v-btn-height: 24px;

    .icon-play-xs:before {
      color: rgb(var(--v-theme-white));
    }
  }
}

.icon-warning-xs:before {
  color: rgb(var(--v-theme-primary));
}
</style>
